import { createAsyncThunk } from "@reduxjs/toolkit";
import { ExtractImportRequest } from "../contracts/ExtractImportRequest";
import { ImportExtractionResponse } from "../contracts/ImportExtractionResponse";
import { ImportResultResponse } from "../contracts/ImportResultResponse";
import { ImportProposal } from "../models/ImportProposal";
import { ImportResult } from "../models/ImportResult";
import { ImportProposalResponseMapper } from "../mappers/ImportProposalResponseMapper";
import { ConfirmImportRequestMapper } from "../mappers/ConfirmImportRequestMapper";
import { ImportResultResponseMapper } from "../mappers/ImportResultResponseMapper";
import { ApiErrorResponse } from "../../../api/types/ApiErrorResponse";
import { toApiError } from "../../../api/utils/errorHandling";
import axiosInstance from "../../axiosInstance/axiosInstance";
import { fetchGroups, fetchMembers } from "../../members/slice-extensions/members-slice-extension";
import { ImportExtractionStatus } from "../contracts/ImportExtractionStatus";
import { extractionProgressed } from "./assistant-import-actions";

const proposalMapper = new ImportProposalResponseMapper();
const confirmMapper = new ConfirmImportRequestMapper();
const resultMapper = new ImportResultResponseMapper();

const EXTRACTIONS_URL = "/assistant/import/extractions";
export const EXTRACTION_POLL_INTERVAL_MS = 2000;
const EXTRACTION_MAX_WAIT_MS = 5 * 60 * 1000;

const delay = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

/** Polls a background extraction until it is final, or gives up after five minutes. */
async function waitForExtraction(
  id: string,
  signal: AbortSignal,
  onStatus: (status: ImportExtractionStatus) => void,
): Promise<ImportExtractionResponse | null> {
  const deadline = Date.now() + EXTRACTION_MAX_WAIT_MS;
  while (Date.now() < deadline && !signal.aborted) {
    await delay(EXTRACTION_POLL_INTERVAL_MS);
    const { data } = await axiosInstance.get<ImportExtractionResponse>(`${EXTRACTIONS_URL}/${id}`);
    if (signal.aborted) return null;
    onStatus(data.status);
    if (data.status === "Succeeded" || data.status === "Failed") return data;
  }
  return null;
}

export const extractImportProposal = createAsyncThunk<
  ImportProposal,
  ExtractImportRequest,
  { rejectValue: ApiErrorResponse }
>(
  "assistantImport/extractImportProposal",
  async (request: ExtractImportRequest, { dispatch, rejectWithValue, signal }) => {
    try {
      const started = await axiosInstance.post<ImportExtractionResponse>(EXTRACTIONS_URL, request);
      dispatch(extractionProgressed(started.data.status));
      const extraction = await waitForExtraction(started.data.id, signal, (status) =>
        dispatch(extractionProgressed(status)),
      );
      if (extraction?.status === "Succeeded" && extraction.proposal) {
        return proposalMapper.toImportProposal(extraction.proposal);
      }
      return rejectWithValue({
        message: extraction?.errorMessage ?? "The assistant took too long. Please try again.",
      });
    } catch (error: unknown) {
      return rejectWithValue(toApiError(error, "Failed to analyze the material"));
    }
  },
);

export const confirmImport = createAsyncThunk<
  ImportResult,
  ImportProposal,
  { rejectValue: ApiErrorResponse }
>(
  "assistantImport/confirmImport",
  async (proposal: ImportProposal, { dispatch, rejectWithValue }) => {
    try {
      const response = await axiosInstance.post<ImportResultResponse>(
        "/assistant/import/confirm",
        confirmMapper.toConfirmImportRequest(proposal),
      );
      // The members/groups directory is loaded once per session; refresh it so
      // rosters resolve the just-imported names without a page reload.
      dispatch(fetchMembers());
      dispatch(fetchGroups());
      return resultMapper.toImportResult(response.data);
    } catch (error: unknown) {
      return rejectWithValue(toApiError(error, "Failed to create the imported items"));
    }
  },
);
